import { Component, ElementRef, HostListener, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { Router } from '@angular/router';
import { Subject, Subscription, of } from 'rxjs';
import { debounceTime, distinctUntilChanged, switchMap, catchError } from 'rxjs/operators';
import { CmsService } from 'src/app/core/cms.service';
import { SearchService } from 'src/app/core/search.service';
import { LanguageService } from 'src/app/core/language.service';
import { SearchResult, SearchResultType } from 'src/app/core/cms.models';

interface ResultGroup { type: SearchResultType; labelEn: string; labelAr: string; icon: string; items: SearchResult[]; }

@Component({
  selector: 'app-search-overlay',
  templateUrl: './search-overlay.component.html',
  styleUrls: ['./search-overlay.component.css']
})
export class SearchOverlayComponent implements OnInit, OnDestroy {
  @ViewChild('searchInput') searchInput?: ElementRef<HTMLInputElement>;

  open = false;
  term = '';
  loading = false;
  searched = false;
  groups: ResultGroup[] = [];
  activeIndex = -1;   // highlighted result for keyboard navigation

  private query$ = new Subject<string>();
  private subs = new Subscription();

  constructor(public search: SearchService, public lang: LanguageService,
              private cms: CmsService, private router: Router) {}

  ngOnInit(): void {
    this.subs.add(this.search.open$.subscribe((o) => {
      this.open = o;
      if (typeof document !== 'undefined') document.body.style.overflow = o ? 'hidden' : '';
      if (o) setTimeout(() => this.searchInput?.nativeElement.focus(), 50);
      else this.reset();
    }));

    this.subs.add(this.query$.pipe(
      debounceTime(250),
      distinctUntilChanged(),
      switchMap((q) => q.trim().length < 2 ? of([] as SearchResult[])
        : this.cms.search(q.trim()).pipe(catchError(() => of([] as SearchResult[]))))
    ).subscribe((results) => {
      this.groups = this.groupResults(results);
      this.loading = false;
      this.searched = this.term.trim().length >= 2;
      this.activeIndex = this.total > 0 ? 0 : -1;
    }));
  }

  ngOnDestroy(): void {
    this.subs.unsubscribe();
    if (typeof document !== 'undefined') document.body.style.overflow = '';
  }

  onInput(): void {
    this.loading = this.term.trim().length >= 2;
    if (this.term.trim().length < 2) { this.groups = []; this.searched = false; this.activeIndex = -1; }
    this.query$.next(this.term);
  }

  @HostListener('document:keydown.escape')
  onEsc(): void { if (this.open) this.search.close(); }

  /** Open with ⌘K / Ctrl+K or "/"; navigate results with ↑ ↓ and open with Enter. */
  @HostListener('document:keydown', ['$event'])
  onKeydown(e: KeyboardEvent): void {
    if (!this.open) {
      if (e.key.toLowerCase() === 'k' && (e.metaKey || e.ctrlKey)) { e.preventDefault(); this.search.open(); }
      else if (e.key === '/' && !this.isTypingTarget(e.target)) { e.preventDefault(); this.search.open(); }
      return;
    }
    if (e.key === 'ArrowDown') { e.preventDefault(); this.move(1); }
    else if (e.key === 'ArrowUp') { e.preventDefault(); this.move(-1); }
    else if (e.key === 'Enter') {
      const r = this.flatResults[this.activeIndex];
      if (r) { e.preventDefault(); this.go(r); }
    }
  }

  close(): void { this.search.close(); }

  /** Total result count across all groups. */
  get total(): number { return this.groups.reduce((n, g) => n + g.items.length, 0); }

  /** Results flattened in render order (for keyboard navigation). */
  get flatResults(): SearchResult[] {
    return this.groups.reduce((a, g) => a.concat(g.items), [] as SearchResult[]);
  }
  isActive(r: SearchResult): boolean { return this.flatResults[this.activeIndex] === r; }

  private move(d: number): void {
    const n = this.total;
    if (n === 0) return;
    this.activeIndex = (this.activeIndex + d + n) % n;
    setTimeout(() => {
      (document.querySelector('.mm-so-item.active') as HTMLElement | null)?.scrollIntoView({ block: 'nearest' });
    });
  }

  private isTypingTarget(t: EventTarget | null): boolean {
    const el = t as HTMLElement | null;
    if (!el) return false;
    return el.tagName === 'INPUT' || el.tagName === 'TEXTAREA' || el.isContentEditable;
  }

  /** Navigate to the selected result's page, then close. */
  go(r: SearchResult): void {
    this.search.close();
    switch (r.type) {
      case 'blog':    this.router.navigate(['/itblogs/blogs/detail'], { queryParams: { slug: r.slug } }); break;
      case 'service': this.router.navigate(['/itsolutions/services/detail'], { queryParams: { slug: r.slug } }); break;
      case 'job':     this.router.navigate(['/company/careers/detail'], { queryParams: { slug: r.slug } }); break;
      case 'faq':     this.router.navigate(['/company/faq']); break;
    }
  }

  private reset(): void {
    this.term = '';
    this.groups = [];
    this.loading = false;
    this.searched = false;
    this.activeIndex = -1;
  }

  private groupResults(results: SearchResult[]): ResultGroup[] {
    const defs: ResultGroup[] = [
      { type: 'service', labelEn: 'Services', labelAr: 'الخدمات', icon: 'fa-cube', items: [] },
      { type: 'blog',    labelEn: 'Blog',     labelAr: 'المدونة', icon: 'fa-newspaper-o', items: [] },
      { type: 'job',     labelEn: 'Careers',  labelAr: 'الوظائف', icon: 'fa-briefcase', items: [] },
      { type: 'faq',     labelEn: 'FAQ',      labelAr: 'الأسئلة الشائعة', icon: 'fa-question-circle', items: [] }
    ];
    for (const r of results) defs.find((g) => g.type === r.type)?.items.push(r);
    return defs.filter((g) => g.items.length > 0);
  }
}
